// React i hooks
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

// API servisi i tipovi
import { DataWithPagination } from "services/api";
import { initialDataWithPagination } from "services/api/consts";
import { getReportsAPI } from "services/api/reports/api";
import { ReportRow, ReportSearchFormValues } from "services/api/reports/types";

// Navigacija
import { Routes } from "../../navigation";
import { Routes as ReportRoutes } from "ui/modules/reports/navigation";

// Hook-ovi
import { useSelectedItemsChanges } from "ui/components/Table/hooks";

// Tipovi
import { ReportSearchPageCmp, ReportSearchPageProps } from "./types";

// UI komponente
import { Breadcrumb } from "ui/components/Breadcrumb";
import { PageHeader } from "ui/components/PageHeader";
import { PageTitle } from "ui/components/PageTitle";
import { Pagination } from "services/search";
import { NoResults } from "ui/components/NoResults";

// Konstante
import { REPORT_SEARCH_PAGE_TITLE } from "ui/components/PageTitle/consts";

// Helpers
import { transformRoute } from "helpers";

// Lokalne komponente
import ReportSearchFilters from "./components/ReportSearchFilters/ReportSearchFilters";
import ReportSearchResults from "./components/ReportSearchResults/ReportSearchResults";


const ReportSearchPage: ReportSearchPageCmp = (
  props: ReportSearchPageProps
) => {
  const [searchResults, setSearchResults] = useState<
    DataWithPagination<ReportRow>
  >(initialDataWithPagination);

  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const [reportId, setReportId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showAddNewModal, setShowAddNewModal] = useState<boolean>(false);

  const navigate = useNavigate();

  useEffect(() => {
    fetchSearchResult(searchResults.pagination);
  }, []);

  const fetchSearchResult = async (
    pagination: Pagination,
    name?: string | null
  ) => {
    setIsLoading(true);
    try {
      const results = await getReportsAPI(pagination, name);

      setSearchResults(results);
    } catch {
      setSearchResults(initialDataWithPagination);
      setIsLoading(false);
      return;
    }
    setIsLoading(false);
  };

  const handleRowClick = useCallback(async (id: string) => {
    navigate(
      transformRoute(ReportRoutes.ReportDetailPageRouter, {
        id,
      })
    );
  }, []);

  const handleAddNew = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      setReportId(null);
      setShowAddNewModal(true);
    },
    []
  );

  const handleSearchChange = useCallback(
    (searchFormValues: ReportSearchFormValues) => {
      fetchSearchResult(searchResults.pagination, searchFormValues.search);
    },
    []
  );

  const handleEditSuccess = useCallback(() => {
    fetchSearchResult(searchResults.pagination);
    setReportId(null);
    setShowAddNewModal(false);
  }, []);

  const handleSelectedChange = useSelectedItemsChanges(
    selectedRows,
    setSelectedRows
  );

  const handlePaginationChange = (newPagination: Pagination) => {
    fetchSearchResult(newPagination);
  };

  return (
    <>
      <div className="">
        <div className="bg-white rounded-t-md border border-b mb-2">
          <PageHeader>
            <PageTitle
              title={REPORT_SEARCH_PAGE_TITLE.text}
              icon={REPORT_SEARCH_PAGE_TITLE.icon}
              iconColor={REPORT_SEARCH_PAGE_TITLE.iconColor}
            />
            <Breadcrumb />
          </PageHeader>
        </div>
        <div className="px-4">
          <ReportSearchFilters
            onAddNew={handleAddNew}
            onSearchChange={handleSearchChange}
          />
          {!isLoading && searchResults.pagination.totalPages === 0 ? (
            <NoResults message="" />
          ) : (
            <ReportSearchResults
              searchResults={searchResults}
              selectedRows={selectedRows}
              isLoading={isLoading}
              onRowClick={handleRowClick}
              onPaginationChange={handlePaginationChange}
              onSelectedChange={handleSelectedChange}
            />
          )}
        </div>
      </div>
    </>
  );
};

ReportSearchPage.route = Routes.ReportSearchPageRouter;

export default ReportSearchPage;
